import React, { useRef, useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tabs,
  Tab,
  Button,
  IconButton,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Popover,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  ShowChart,
  MonetizationOn,
  Assessment,
  Archive,
  Refresh,
  ViewModule,
  TableRows,
  ContentCopy,
} from "@mui/icons-material";
import { useAppContext } from "../context/AppContext";
import { useStockData } from "../context/StockDataContext";
import { PortfolioSummary } from "../types";
import { format } from "date-fns";
import { marketIndexService } from "../services/marketIndexService";
import {
  HoldingsCalculationService,
  HoldingCalculation,
  RemainingLot,
} from "../services/holdingsCalculationService";
import { StockService } from "../services/stockService";
import PageHeader from "./common/PageHeader";
import { logger } from "../utils/logger";
import { StockNameManager } from "../services/stockNameManager";
import { useNotification } from "../context/NotificationContext";
import {
  staticStockDataService,
  StaticStockInfo,
} from "../services/staticStockDataService";

interface HoldingData {
  symbol: string;
  name: string;
  quantity: number;
  avgCost: number;
  currentPrice: number | null;
  investmentCost: number;
  marketValue: number;
  unrealizedGain: number;
  unrealizedGainPercent: number;
  dividends: number;
  realizedGain: number;
  totalGain: number; // 總損益（含息）= 未實現 + 已實現 + 股利
  totalGainPercent: number; // 總報酬率（含息）
  costPerLotWithDividend: number;
  costPerLotWithoutDividend: number;
  costLots: RemainingLot[]; // 分次購買批次明細（FIFO 扣減後剩餘）
}

interface HistoricalHoldingData {
  symbol: string;
  name: string;
  totalBuyQuantity: number;
  totalSellQuantity: number;
  totalBuyAmount: number;
  totalSellAmount: number;
  dividends: number;
  realizedGain: number;
  realizedGainPercent: number;
  avgBuyPrice: number;
  avgSellPrice: number;
  totalProfit: number; // 包含股利的總收益
  lastTransactionDate: Date; // 用於排序：最近清倉時間
}

// 表頭欄位元件：主標籤 + 第二行小字標注（含息/價差），可附 Tooltip 說明
interface StatHeaderCellProps {
  label: string;
  qualifier?: string;
  tooltip?: string;
}

const StatHeaderCell: React.FC<StatHeaderCellProps> = ({
  label,
  qualifier,
  tooltip,
}) => {
  const content = (
    <Box component="span" sx={{ display: "inline-block", textAlign: "right" }}>
      <Box component="span" sx={{ display: "block", fontWeight: "bold" }}>
        {label}
      </Box>
      {qualifier && (
        <Box
          component="span"
          sx={{
            display: "block",
            fontSize: 11,
            fontWeight: "normal",
            color: "text.secondary",
            lineHeight: 1.4,
          }}
        >
          {qualifier}
        </Box>
      )}
    </Box>
  );

  return (
    <TableCell align="right">
      {tooltip ? (
        <Tooltip title={tooltip}>
          <Box component="span" sx={{ cursor: "help" }}>
            {content}
          </Box>
        </Tooltip>
      ) : (
        content
      )}
    </TableCell>
  );
};

// 卡片標籤用的小標注（含息/價差）
const QualifierTag: React.FC<{ text: string }> = ({ text }) => (
  <Box
    component="span"
    sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}
  >
    {text}
  </Box>
);

// 可點擊的成本數字：開啟分次購買成本明細 Popover
const CostBreakdownTrigger: React.FC<{
  value: number;
  onOpen: (event: React.SyntheticEvent<HTMLElement>) => void;
}> = ({ value, onOpen }) => (
  <Tooltip title="點擊查看分次購買成本明細">
    <Typography
      variant="body1"
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(event);
        }
      }}
      role="button"
      tabIndex={0}
      sx={{
        cursor: "pointer",
        display: "inline-block",
        textDecoration: "underline dotted",
        textUnderlineOffset: 3,
        "&:hover": { color: "primary.main" },
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
      }}
    >
      ${Math.round(value).toLocaleString()}
    </Typography>
  </Tooltip>
);

// 分次購買成本明細 Popover 內容（批次加總 = 投資成本）
const CostBreakdownContent: React.FC<{ holding: HoldingData }> = ({
  holding,
}) => (
  <Box sx={{ p: 2, minWidth: 340, maxWidth: 400 }}>
    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
      分次購買成本（{holding.symbol} {holding.name}）
    </Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: "bold" }}>日期</TableCell>
          <TableCell align="right" sx={{ fontWeight: "bold" }}>
            股數
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: "bold" }}>
            均價
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: "bold" }}>
            成本(含手續費)
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {holding.costLots.map((lot, index) => (
          <TableRow key={`${new Date(lot.date).getTime()}-${index}`}>
            <TableCell>{format(new Date(lot.date), "yyyy/MM/dd")}</TableCell>
            <TableCell align="right">
              {Math.round(lot.quantity).toLocaleString()}
            </TableCell>
            <TableCell align="right">
              {lot.source === "stock_increase" ? (
                <QualifierTag text="股票股利" />
              ) : (
                `$${lot.price}`
              )}
            </TableCell>
            <TableCell align="right">
              ${lot.cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell colSpan={3} sx={{ fontWeight: "bold" }}>
            合計（投資成本）
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: "bold" }}>
            ${Math.round(holding.investmentCost).toLocaleString()}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </Box>
);

const Holdings: React.FC = () => {
  const { state, mergeStockPrices } = useAppContext();
  const stockData = useStockData();
  const [tabValue, setTabValue] = useState(0);
  const [isRefreshingPrices, setIsRefreshingPrices] = useState(false);
  const isRefreshingPricesRef = useRef(false);
  const isMountedRef = useRef(true);
  const { transactions, stocks, currentAccountId, currentUser, authUser } =
    state;
  const hasAccount = currentAccountId || currentUser;
  const theme = useTheme();
  const prefersCardView = useMediaQuery(theme.breakpoints.down("md"));
  const [currentHoldingsView, setCurrentHoldingsView] = useState<
    "table" | "card"
  >(() => (prefersCardView ? "card" : "table"));
  const [historicalHoldingsView, setHistoricalHoldingsView] = useState<
    "table" | "card"
  >(() => (prefersCardView ? "card" : "table"));
  const { showSuccess, showError, showInfo } = useNotification();
  const [stockMarketInfo, setStockMarketInfo] = useState<
    Map<string, StaticStockInfo>
  >(new Map());

  // 分次購買成本明細 Popover
  const [costBreakdown, setCostBreakdown] = useState<{
    holding: HoldingData;
    anchorEl: HTMLElement;
  } | null>(null);

  const handleOpenCostBreakdown =
    (holding: HoldingData) => (event: React.SyntheticEvent<HTMLElement>) => {
      setCostBreakdown({ holding, anchorEl: event.currentTarget });
    };

  const handleCloseCostBreakdown = () => setCostBreakdown(null);

  const getMarketColor = (market?: string) => {
    switch (market) {
      case "上市":
        return { bg: "#e3f2fd", border: "#1976d2" };
      case "上櫃":
        return { bg: "#fff3e0", border: "#f57c00" };
      case "興櫃":
        return { bg: "#e8f5e9", border: "#388e3c" };
      default:
        return { bg: "#f5f5f5", border: "#9e9e9e" };
    }
  };

  // 過濾當前帳戶的交易（只過濾現金進出，保留股票異動）
  const filteredTransactions = React.useMemo(() => {
    return state.transactions.filter(
      (t) =>
        t.accountId === state.currentAccountId &&
        t.type !== "cash_deposit" &&
        t.type !== "cash_withdrawal",
    );
  }, [state.transactions, state.currentAccountId]);

  // 交易資料更新時關閉明細 Popover，避免顯示過期的批次快照
  React.useEffect(() => {
    setCostBreakdown(null);
  }, [filteredTransactions]);

  // UI-04:單次計算同時取得 current + historical(相同輸入重算兩次是純浪費,
  // 每 60 秒現價輪詢後會觸發兩次全量重算)
  const holdingsResult = React.useMemo(() => {
    return HoldingsCalculationService.calculateHoldingsFromTransactions(
      filteredTransactions,
      state.stocks,
      { includeFees: true, includeHistorical: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions, state.stocks]);

  const { currentHoldings } = holdingsResult;

  useEffect(() => {
    const loadMarketInfo = async () => {
      // UI-05:平行抓取(取代序列 await 的 N+1)
      const results = await Promise.allSettled(
        currentHoldings.map((holding) =>
          staticStockDataService.getStockInfo(holding.symbol),
        ),
      );
      const marketMap = new Map<string, StaticStockInfo>();
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          marketMap.set(result.value.symbol, result.value);
        }
      });
      setStockMarketInfo(marketMap);
    };
    if (currentHoldings.length > 0) {
      loadMarketInfo();
    }
    // UI-05:以 symbol 集合為 key 去重觸發(現價輪詢改變 price 不需重跑靜態資訊載入)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHoldings.map((h) => h.symbol).join(',')]);

  const activeHoldingSymbols = React.useMemo(() => {
    const symbols = currentHoldings
      .filter((holding) => holding.currentQuantity > 0)
      .map((holding) => holding.symbol);
    return Array.from(new Set(symbols));
  }, [currentHoldings]);

  // 以「內容」為鍵穩定陣列 identity：currentHoldings 依 state.stocks 計算，
  // 每次價格更新 dispatch 後都會產生新物件，若直接使用 activeHoldingSymbols 會讓
  // updateStockPrices 身份改變 → debounce effect 重觸發 → 重複查價的無限循環
  const activeHoldingSymbolsKey = activeHoldingSymbols.join(",");
  const stableHoldingSymbols = React.useMemo(
    () => (activeHoldingSymbolsKey ? activeHoldingSymbolsKey.split(",") : []),
    [activeHoldingSymbolsKey],
  );

  // 以 ref 讀取最新 stocks，避免 stocks 變動 (每次輪詢 dispatch 後) 造成
  // updateStockPrices 身份改變 → debounce effect 重觸發 → 重複查價的循環
  const stocksRef = React.useRef(stocks);
  React.useEffect(() => {
    stocksRef.current = stocks;
  }, [stocks]);

  const updateStockPrices = React.useCallback(async () => {
    // 檢查是否已經在進行中
    if (isRefreshingPricesRef.current) {
      logger.holdings.info("⏳ 已有持股現價更新進行中，略過新的請求");
      return;
    }

    // 檢查全局 StockDataContext 是否正在加載
    if (stockData.isLoading) {
      logger.holdings.info("⏳ 全局股票資料正在加載中，略過本次請求");
      return;
    }

    if (stableHoldingSymbols.length === 0) {
      logger.holdings.info("ℹ️ 無持股需要更新現價，略過請求");
      return;
    }

    isRefreshingPricesRef.current = true;
    setIsRefreshingPrices(true);

    try {
      logger.holdings.info("📈 庫存頁面：開始更新持股現價", {
        currentAccountId,
        transactionCount: transactions.length,
        activeHoldingCount: stableHoldingSymbols.length,
      });

      logger.holdings.info("🔍 將透過 API 更新持股現價", {
        symbols: stableHoldingSymbols,
        count: stableHoldingSymbols.length,
      });

      const updatedStocks = await StockService.getOptimizedStockData(
        stableHoldingSymbols,
        stocksRef.current,
      );

      if (updatedStocks.length === 0) {
        logger.holdings.warn("⚠️ 無法取得任何持股現價");
        return;
      }

      logger.holdings.info("✅ 成功取得持股現價", {
        updatedCount: updatedStocks.length,
      });

      // ARCH-01:經由語意化方法合併現價(取代直接 dispatch SET_STOCKS 全量覆寫,
      // 避免被其他寫入者的快取資料蓋掉);只在組件還掛載時更新
      if (isMountedRef.current) {
        mergeStockPrices(updatedStocks);
      }
    } catch (error) {
      logger.holdings.error("❌ 庫存頁面：更新持股現價失敗", error);
    } finally {
      isRefreshingPricesRef.current = false;
      // 只在組件還掛載時才更新 state，避免內存洩漏和狀態不一致
      if (isMountedRef.current) {
        setIsRefreshingPrices(false);
      }
    }
  }, [
    transactions.length,
    stableHoldingSymbols,
    currentAccountId,
    mergeStockPrices,
    stockData.isLoading,
  ]);

  // 組件掛載/卸載管理
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 當 component mount 或依賴變化時記錄並更新股票現價
  React.useEffect(() => {
    logger.holdings.debug("🚀 Holdings 組件載入或依賴變更", {
      currentAccountId,
      hasAuthUser: !!authUser,
      transactionsCount: transactions.length,
    });

    if (!hasAccount || stableHoldingSymbols.length === 0) {
      return;
    }

    const refreshTimer = setTimeout(() => {
      if (isMountedRef.current) {
        updateStockPrices();
      }
    }, 500);

    return () => clearTimeout(refreshTimer);
  }, [
    hasAccount,
    stableHoldingSymbols,
    updateStockPrices,
    currentAccountId,
    authUser,
    transactions.length,
  ]);

  // 開盤期間每 60 秒自動輪詢持股現價
  // 注意：一律建立 interval、由每次 tick 內檢查交易時段，
  // 避免在盤前掛載頁面時 (effect 只在掛載/依賴變更時執行) 開盤後永遠不會啟動輪詢
  const updateStockPricesRef = React.useRef(updateStockPrices);
  React.useEffect(() => {
    updateStockPricesRef.current = updateStockPrices;
  }, [updateStockPrices]);

  React.useEffect(() => {
    if (!hasAccount) {
      return;
    }

    const pollTimer = setInterval(() => {
      if (!isMountedRef.current) {
        return;
      }
      // 交易時段輪詢現價；13:30-14:30 續 poll 一段時間以取得最終收盤成交價；
      // 之後價格不再變動，改由掛載/刷新時的一次性查價取得當日收盤
      if (
        marketIndexService.isMarketOpen() ||
        marketIndexService.isMarketRecentlyClosed()
      ) {
        updateStockPricesRef.current();
      }
    }, 60000);

    // 收盤後輪詢已停止，但「盤前載入、tab 沉睡過整個交易日」或
    // 「掛載當下 MIS 短暫失敗 fallback 昨收」的頁面會停留在舊價；
    // 分頁重新可見時補一次查價 (節流 5 分鐘，避免頻繁切換分頁打 API)
    let lastOffMarketRefresh = 0;
    const handleRefocusRefresh = () => {
      if (!isMountedRef.current || document.visibilityState !== 'visible') {
        return;
      }
      const inPollingSession =
        marketIndexService.isMarketOpen() ||
        marketIndexService.isMarketRecentlyClosed();
      if (inPollingSession) {
        return; // 交易時段由 60 秒輪詢覆蓋
      }
      const now = Date.now();
      if (now - lastOffMarketRefresh < 5 * 60 * 1000) {
        return;
      }
      lastOffMarketRefresh = now;
      updateStockPricesRef.current();
    };

    document.addEventListener('visibilitychange', handleRefocusRefresh);
    window.addEventListener('focus', handleRefocusRefresh);

    return () => {
      clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', handleRefocusRefresh);
      window.removeEventListener('focus', handleRefocusRefresh);
    };
  }, [hasAccount]);

  // 轉換為元件所需格式（依最新交易時間由新到舊排序，並去重複）
  const holdings: HoldingData[] = React.useMemo(() => {
    const uniqueBySymbol = new Map<string, HoldingCalculation>();
    currentHoldings.forEach((holding) => {
      uniqueBySymbol.set(holding.symbol, holding);
    });
    return Array.from(uniqueBySymbol.values())
      .sort(
        (a, b) =>
          new Date(b.lastTransactionDate).getTime() -
          new Date(a.lastTransactionDate).getTime(),
      )
      .map((holding: HoldingCalculation) => ({
        symbol: holding.symbol,
        name: StockNameManager.getStockName(holding.symbol),
        quantity: holding.currentQuantity,
        avgCost: holding.avgCost,
        currentPrice: holding.currentPrice,
        investmentCost: holding.investmentCost,
        marketValue: holding.marketValue,
        unrealizedGain: holding.unrealizedGain,
        unrealizedGainPercent: holding.unrealizedGainPercent,
        dividends: holding.netDividendIncome,
        realizedGain: holding.realizedGain,
        totalGain: holding.totalGain,
        totalGainPercent: holding.totalGainPercent,
        costPerLotWithDividend: holding.costPerShareWithDividend,
        costPerLotWithoutDividend: holding.costPerShareWithoutDividend,
        costLots: holding.remainingLots,
      }));
  }, [currentHoldings]);

  // 使用統一的服務計算過往紀錄(已清倉股票)— UI-04:與 current 共用同一次計算
  const { historicalHoldings: serviceHistoricalHoldings } = holdingsResult;

  // 轉換為元件所需格式（去重複）
  const historicalHoldings: HistoricalHoldingData[] = React.useMemo(() => {
    const uniqueBySymbol = new Map<
      string,
      (typeof serviceHistoricalHoldings)[0]
    >();
    serviceHistoricalHoldings.forEach((holding) => {
      if (holding.symbol && holding.symbol !== "CASH") {
        uniqueBySymbol.set(holding.symbol, holding);
      }
    });
    return (
      Array.from(uniqueBySymbol.values())
        .map((holding) => ({
          symbol: holding.symbol,
          name: StockNameManager.getStockName(holding.symbol), // 使用統一的股票名稱管理器
          totalBuyQuantity: holding.totalBuyQuantity,
          totalSellQuantity: holding.totalSellQuantity,
          totalBuyAmount: holding.totalBuyAmount,
          totalSellAmount: holding.totalSellAmount,
          dividends: holding.dividends,
          realizedGain: holding.realizedGain,
          realizedGainPercent: holding.realizedGainPercent,
          avgBuyPrice: holding.avgBuyPrice,
          avgSellPrice: holding.avgSellPrice,
          totalProfit: holding.totalProfit,
          lastTransactionDate: (holding as any).lastTransactionDate,
        }))
        // 新增排序：依最近清倉時間由新到舊
        .sort(
          (a, b) =>
            new Date(b.lastTransactionDate).getTime() -
            new Date(a.lastTransactionDate).getTime(),
        )
    );
  }, [serviceHistoricalHoldings]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCurrentHoldingsViewChange = (
    event: React.MouseEvent<HTMLElement>,
    nextView: "table" | "card" | null,
  ) => {
    if (nextView !== null) {
      setCurrentHoldingsView(nextView);
    }
  };

  const handleHistoricalHoldingsViewChange = (
    event: React.MouseEvent<HTMLElement>,
    nextView: "table" | "card" | null,
  ) => {
    if (nextView !== null) {
      setHistoricalHoldingsView(nextView);
    }
  };

  const handleCopyHoldingsSummary = React.useCallback(async () => {
    if (holdings.length === 0) {
      showInfo("目前沒有可複製的持股資料");
      return;
    }

    const summaryText = holdings
      .map(
        (holding) =>
          `${holding.name}(${holding.symbol}) ${holding.quantity.toLocaleString()}`,
      )
      .join("\n");

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(summaryText);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = summaryText;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      showSuccess("已複製持股文案");
      logger.holdings.info("📋 已複製目前持股文案", {
        lineCount: holdings.length,
      });
    } catch (error) {
      logger.holdings.error("❌ 複製持股文案失敗", error);
      showError("複製失敗，請稍後再試");
    }
  }, [holdings, showError, showSuccess, showInfo]);

  // 僅針對有現價的持股進行統計
  const pricedHoldings = React.useMemo(
    () =>
      holdings.filter(
        (holding): holding is HoldingData & { currentPrice: number } =>
          holding.currentPrice !== null,
      ),
    [holdings],
  );

  const totalMarketValue = pricedHoldings.reduce(
    (sum, holding) => sum + holding.marketValue,
    0,
  );
  const totalInvestmentCost = pricedHoldings.reduce(
    (sum, holding) => sum + holding.investmentCost,
    0,
  );
  const totalGainLoss = totalMarketValue - totalInvestmentCost;
  const totalGainLossPercent =
    totalInvestmentCost > 0 ? (totalGainLoss / totalInvestmentCost) * 100 : 0;

  // 找出表現最佳和最差的持股
  let topPerformerHolding: HoldingData | null = null;
  let worstPerformerHolding: HoldingData | null = null;

  if (pricedHoldings.length > 0) {
    topPerformerHolding = pricedHoldings.reduce(
      (currentBestHolding, holding) => {
        return holding.unrealizedGainPercent >
          currentBestHolding.unrealizedGainPercent
          ? holding
          : currentBestHolding;
      },
      pricedHoldings[0],
    );

    worstPerformerHolding = pricedHoldings.reduce(
      (currentWorstHolding, holding) => {
        return holding.unrealizedGainPercent <
          currentWorstHolding.unrealizedGainPercent
          ? holding
          : currentWorstHolding;
      },
      pricedHoldings[0],
    );
  }

  const portfolioSummary: PortfolioSummary = {
    totalHoldings: pricedHoldings.length,
    totalMarketValue,
    totalCost: totalInvestmentCost,
    totalGainLoss,
    totalGainLossPercent,
    topPerformer: topPerformerHolding
      ? {
          symbol: topPerformerHolding.symbol,
          quantity: topPerformerHolding.quantity,
          avgCost: topPerformerHolding.avgCost,
        }
      : null,
    worstPerformer: worstPerformerHolding
      ? {
          symbol: worstPerformerHolding.symbol,
          quantity: worstPerformerHolding.quantity,
          avgCost: worstPerformerHolding.avgCost,
        }
      : null,
  };

  // 找到對應的實際holding資料，用於顯示
  const getHoldingData = (portfolioHolding: any) => {
    if (portfolioHolding?.symbol) {
      return holdings.find((h) => h.symbol === portfolioHolding.symbol);
    }
    return null;
  };

  const holdingsSubtitle = React.useMemo(() => {
    if (pricedHoldings.length === holdings.length) {
      return `投資組合持股明細 • ${holdings.length}檔股票`;
    }
    return `投資組合持股明細 • ${holdings.length}檔股票（${pricedHoldings.length}檔有現價）`;
  }, [holdings.length, pricedHoldings.length]);

  // 同時檢查 state 和 ref，確保按鈕狀態與實際運行狀態一致
  const disableRefreshButton =
    activeHoldingSymbols.length === 0 ||
    isRefreshingPrices ||
    isRefreshingPricesRef.current;

  if (!hasAccount) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          請選擇交易帳戶
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="庫存狀況"
        icon={ShowChart}
        subtitle={holdingsSubtitle}
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={!isRefreshingPrices ? <Refresh /> : undefined}
            onClick={updateStockPrices}
            disabled={disableRefreshButton}
            sx={{ minWidth: 160 }}
          >
            {isRefreshingPrices ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                更新中…
              </Box>
            ) : (
              "重新取得現價"
            )}
          </Button>
        }
      />

      {/* Summary Cards */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 2, sm: 3 },
          mb: 4,
          "& > *": {
            flex: { xs: "1 1 100%", sm: "1 1 280px", md: "1 1 280px" },
            minWidth: { xs: "100%", sm: "280px" },
          },
        }}
      >
        <Card
          sx={{
            background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <MonetizationOn sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                總市值
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              ${Math.round(portfolioSummary.totalMarketValue).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              持股總價值
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: "linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <AccountBalance sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                總成本
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              ${Math.round(portfolioSummary.totalCost).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              購入總成本
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            background:
              portfolioSummary.totalGainLoss >= 0
                ? "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)"
                : "linear-gradient(135deg, #f44336 0%, #e57373 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              {portfolioSummary.totalGainLoss >= 0 ? (
                <TrendingUp sx={{ fontSize: 32, mr: 1 }} />
              ) : (
                <TrendingDown sx={{ fontSize: 32, mr: 1 }} />
              )}
              <Typography variant="h6" fontWeight="bold">
                損益(價差)
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {portfolioSummary.totalGainLoss >= 0 ? "+" : ""}$
              {Math.round(portfolioSummary.totalGainLoss).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {portfolioSummary.totalGainLossPercent.toFixed(2)}% · 不含股利
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: "linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Assessment sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                持股檔數
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {portfolioSummary.totalHoldings}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              種股票
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Performance Summary */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 2, sm: 3 },
          mb: 4,
          "& > *": {
            flex: { xs: "1 1 100%", md: "1 1 400px" },
            minWidth: { xs: "100%", md: "400px" },
          },
        }}
      >
        {portfolioSummary.topPerformer && (
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              color="success.main"
            >
              表現最佳
            </Typography>
            {(() => {
              const topHolding = getHoldingData(portfolioSummary.topPerformer);
              if (!topHolding) return null;

              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {topHolding.symbol}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {topHolding.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={`+${topHolding.unrealizedGainPercent.toFixed(2)}%`}
                      color="success"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(topHolding.unrealizedGainPercent, 100)}
                    color="success"
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    獲利: $
                    {Math.round(topHolding.unrealizedGain).toLocaleString()}
                  </Typography>
                </>
              );
            })()}
          </Paper>
        )}

        {portfolioSummary.worstPerformer && (
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              color="error.main"
            >
              表現最差
            </Typography>
            {(() => {
              const worstHolding = getHoldingData(
                portfolioSummary.worstPerformer,
              );
              if (!worstHolding) return null;

              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {worstHolding.symbol}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {worstHolding.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${worstHolding.unrealizedGainPercent.toFixed(2)}%`}
                      color="error"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.abs(
                      Math.max(worstHolding.unrealizedGainPercent, -100),
                    )}
                    color="error"
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    虧損: $
                    {Math.round(worstHolding.unrealizedGain).toLocaleString()}
                  </Typography>
                </>
              );
            })()}
          </Paper>
        )}
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              fontSize: "1rem",
              fontWeight: "bold",
              textTransform: "none",
            },
          }}
        >
          <Tab
            icon={<ShowChart />}
            label={`目前持有 (${holdings.length})`}
            iconPosition="start"
          />
          <Tab
            icon={<Archive />}
            label={`過往紀錄 (${historicalHoldings.length})`}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        /* Current Holdings Table */
        <Paper sx={{ overflow: "hidden", mb: 3 }}>
          <Box
            sx={{
              p: 3,
              pb: 0,
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              📊 持股明細
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                ml: "auto",
              }}
            >
              <IconButton
                aria-label="複製文案"
                onClick={handleCopyHoldingsSummary}
                disabled={holdings.length === 0}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
              <ToggleButtonGroup
                size="small"
                color="primary"
                exclusive
                value={currentHoldingsView}
                onChange={handleCurrentHoldingsViewChange}
              >
                <ToggleButton value="card" aria-label="card-view">
                  <ViewModule sx={{ fontSize: 18, mr: 1 }} />
                  卡片
                </ToggleButton>
                <ToggleButton value="table" aria-label="table-view">
                  <TableRows sx={{ fontSize: 18, mr: 1 }} />
                  表格
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {currentHoldingsView === "table" ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>代號/名稱</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      股數
                    </TableCell>
                    <StatHeaderCell
                      label="投資成本"
                      tooltip="目前持有批次的買入成本（含手續費，已賣出部分依 FIFO 扣減），點擊金額可查看分次明細"
                    />
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      股利
                    </TableCell>
                    <StatHeaderCell
                      label="成本/股"
                      qualifier="含息"
                      tooltip="買入總成本（含手續費）÷ 買入股數，再扣除每股股利"
                    />
                    <StatHeaderCell
                      label="成本/股"
                      qualifier="不含息"
                      tooltip="買入總成本（含手續費）÷ 買入股數"
                    />
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      現價
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      目前價值
                    </TableCell>
                    <StatHeaderCell
                      label="未實現損益"
                      qualifier="價差"
                      tooltip="目前價值 - 投資成本之價差損益，不含股利"
                    />
                    <StatHeaderCell
                      label="已實現損益"
                      qualifier="價差"
                      tooltip="賣出淨收入 - 賣出成本，不含股利"
                    />
                    <StatHeaderCell
                      label="總損益"
                      qualifier="含息"
                      tooltip="未實現損益 + 已實現損益 + 股利收入"
                    />
                    <StatHeaderCell
                      label="報酬率"
                      qualifier="價差"
                      tooltip="未實現損益 ÷ 投資成本，不含股利"
                    />
                    <StatHeaderCell
                      label="總報酬率"
                      qualifier="含息"
                      tooltip="總損益(含息) ÷ 總投入成本（含手續費）"
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {holdings.map((holding) => {
                    const marketInfo = stockMarketInfo.get(holding.symbol);
                    const marketColors = getMarketColor(marketInfo?.market);
                    return (
                      <TableRow
                        key={`${holding.symbol}-current`}
                        sx={{
                          backgroundColor: marketColors.bg,
                          "&:hover": {
                            backgroundColor:
                              marketColors.bg === "#f5f5f5"
                                ? "#f8f9fa"
                                : marketColors.bg,
                          },
                        }}
                      >
                        {/* 代號/名稱 */}
                        <TableCell>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              color="primary"
                            >
                              {holding.symbol}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {holding.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        {/* 股數 */}
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight="medium">
                            {holding.quantity.toLocaleString()}
                          </Typography>
                        </TableCell>
                        {/* 投資成本（點擊查看分次購買明細） */}
                        <TableCell align="right">
                          <CostBreakdownTrigger
                            value={holding.investmentCost}
                            onOpen={handleOpenCostBreakdown(holding)}
                          />
                        </TableCell>
                        {/* 股利 */}
                        <TableCell align="right">
                          <Typography variant="body1" color="primary">
                            ${Math.round(holding.dividends).toLocaleString()}
                          </Typography>
                        </TableCell>
                        {/* 成本/股(含息) */}
                        <TableCell align="right">
                          <Typography variant="body1" color="primary">
                            ${holding.costPerLotWithDividend.toFixed(2)}
                          </Typography>
                        </TableCell>
                        {/* 成本/股(不含息) */}
                        <TableCell align="right">
                          <Typography variant="body1">
                            ${holding.costPerLotWithoutDividend.toFixed(2)}
                          </Typography>
                        </TableCell>
                        {/* 現價 */}
                        <TableCell align="right">
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color={
                              holding.currentPrice === null
                                ? "text.secondary"
                                : "text.primary"
                            }
                          >
                            {holding.currentPrice !== null
                              ? `$${holding.currentPrice.toFixed(2)}`
                              : "N/A"}
                          </Typography>
                          {holding.currentPrice === null && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              無報價資料
                            </Typography>
                          )}
                        </TableCell>
                        {/* 目前價值 */}
                        <TableCell align="right">
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color={
                              holding.currentPrice === null
                                ? "text.secondary"
                                : "text.primary"
                            }
                          >
                            {holding.currentPrice !== null
                              ? `$${Math.round(holding.marketValue).toLocaleString()}`
                              : "N/A"}
                          </Typography>
                          {holding.currentPrice === null && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              需要股價資料
                            </Typography>
                          )}
                        </TableCell>
                        {/* 未實現損益 */}
                        <TableCell align="right">
                          {holding.currentPrice !== null ? (
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              color={
                                holding.unrealizedGain >= 0
                                  ? "success.main"
                                  : "error.main"
                              }
                            >
                              {holding.unrealizedGain >= 0 ? "+" : ""}$
                              {Math.round(
                                holding.unrealizedGain,
                              ).toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              color="text.secondary"
                            >
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        {/* 已實現損益 */}
                        <TableCell align="right">
                          <Typography
                            variant="body1"
                            fontWeight="medium"
                            color={
                              holding.realizedGain >= 0
                                ? "success.main"
                                : "error.main"
                            }
                          >
                            {holding.realizedGain >= 0 ? "+" : ""}$
                            {Math.round(holding.realizedGain).toLocaleString()}
                          </Typography>
                        </TableCell>
                        {/* 總損益(含息) */}
                        <TableCell align="right">
                          {holding.currentPrice !== null ? (
                            <Tooltip title="價差 + 已實現 + 股利，含息總損益">
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                color={
                                  holding.totalGain >= 0
                                    ? "success.main"
                                    : "error.main"
                                }
                              >
                                {holding.totalGain >= 0 ? "+" : ""}$
                                {Math.round(
                                  holding.totalGain,
                                ).toLocaleString()}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="body1"
                              color="text.secondary"
                            >
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        {/* 報酬率(價差) */}
                        <TableCell align="right">
                          {holding.currentPrice !== null ? (
                            <Chip
                              label={`${holding.unrealizedGainPercent >= 0 ? "+" : ""}${holding.unrealizedGainPercent.toFixed(2)}%`}
                              color={
                                holding.unrealizedGainPercent >= 0
                                  ? "success"
                                  : "error"
                              }
                              variant={
                                Math.abs(holding.unrealizedGainPercent) > 10
                                  ? "filled"
                                  : "outlined"
                              }
                              size="small"
                              sx={{
                                fontWeight: "bold",
                              }}
                            />
                          ) : (
                            <Chip
                              label="N/A"
                              color="default"
                              variant="outlined"
                              size="small"
                              sx={{
                                fontWeight: "bold",
                                color: "text.secondary",
                              }}
                            />
                          )}
                        </TableCell>
                        {/* 總報酬率(含息) */}
                        <TableCell align="right">
                          {holding.currentPrice !== null ? (
                            <Tooltip title="總損益(含息) ÷ 總投入成本">
                              <Chip
                                label={`${holding.totalGainPercent >= 0 ? "+" : ""}${holding.totalGainPercent.toFixed(2)}%`}
                                color={
                                  holding.totalGainPercent >= 0
                                    ? "success"
                                    : "error"
                                }
                                variant={
                                  Math.abs(holding.totalGainPercent) > 10
                                    ? "filled"
                                    : "outlined"
                                }
                                size="small"
                                sx={{
                                  fontWeight: "bold",
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              label="N/A"
                              color="default"
                              variant="outlined"
                              size="small"
                              sx={{
                                fontWeight: "bold",
                                color: "text.secondary",
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ px: 3, pb: 3 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {holdings.map((holding) => (
                  <Card
                    key={`${holding.symbol}-card`}
                    variant="outlined"
                    sx={{ height: "100%" }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        p: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="primary"
                          >
                            {holding.symbol}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {holding.name}
                          </Typography>
                        </Box>
                        <Tooltip title="總報酬率(含息)：總損益 ÷ 總投入成本">
                          <Chip
                            label={
                              holding.currentPrice !== null
                                ? `${holding.totalGainPercent >= 0 ? "+" : ""}${holding.totalGainPercent.toFixed(2)}%`
                                : "N/A"
                            }
                            color={
                              holding.currentPrice !== null
                                ? holding.totalGainPercent >= 0
                                  ? "success"
                                  : "error"
                                : "default"
                            }
                            variant={
                              holding.currentPrice !== null
                                ? "filled"
                                : "outlined"
                            }
                            size="small"
                            sx={{ fontWeight: "bold", fontSize: "1rem" }}
                          />
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-around",
                          py: 1,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="body2" color="text.secondary">
                            總損益
                            <QualifierTag text="含息" />
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color={
                              holding.currentPrice !== null
                                ? holding.totalGain >= 0
                                  ? "success.main"
                                  : "error.main"
                                : "text.secondary"
                            }
                          >
                            {holding.currentPrice !== null
                              ? `${holding.totalGain >= 0 ? "+" : ""}$${Math.round(holding.totalGain).toLocaleString()}`
                              : "N/A"}
                          </Typography>
                          {holding.currentPrice !== null && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              價差 {holding.unrealizedGain >= 0 ? "+" : ""}$
                              {Math.round(
                                holding.unrealizedGain,
                              ).toLocaleString()}
                              {holding.realizedGain !== 0 &&
                                ` + 已實現 ${holding.realizedGain >= 0 ? "+" : ""}$${Math.round(holding.realizedGain).toLocaleString()}`}
                              {holding.dividends !== 0 &&
                                ` + 股利 $${Math.round(holding.dividends).toLocaleString()}`}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="body2" color="text.secondary">
                            未實現損益
                            <QualifierTag text="價差" />
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color={
                              holding.currentPrice !== null
                                ? holding.unrealizedGain >= 0
                                  ? "success.main"
                                  : "error.main"
                                : "text.secondary"
                            }
                          >
                            {holding.currentPrice !== null
                              ? `${holding.unrealizedGain >= 0 ? "+" : ""}$${Math.round(holding.unrealizedGain).toLocaleString()}`
                              : "N/A"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            已實現 {holding.realizedGain >= 0 ? "+" : ""}$
                            {Math.round(holding.realizedGain).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          gap: { xs: 2, md: 1 },
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, justifyContent: "space-between", textAlign: { xs: "left", md: "center" }, flex: 1, minWidth: { xs: "auto", md: 120 } }}>
                          <Typography variant="body2" color="text.secondary">
                            股數
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {holding.quantity.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, justifyContent: "space-between", textAlign: { xs: "left", md: "center" }, flex: 1, minWidth: { xs: "auto", md: 120 } }}>
                          <Typography variant="body2" color="text.secondary">
                            現價
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color={holding.currentPrice === null ? "text.secondary" : "text.primary"}
                          >
                            {holding.currentPrice !== null ? `$${holding.currentPrice.toFixed(2)}` : "N/A"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, justifyContent: "space-between", textAlign: { xs: "left", md: "center" }, flex: 1, minWidth: { xs: "auto", md: 120 } }}>
                          <Typography variant="body2" color="text.secondary">
                            成本/股
                            <QualifierTag text="含息" />
                          </Typography>
                          <Tooltip title="點擊查看分次購買成本明細">
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              color="primary"
                              onClick={handleOpenCostBreakdown(holding)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  handleOpenCostBreakdown(holding)(event);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              sx={{
                                cursor: "pointer",
                                display: "inline-block",
                                textDecoration: "underline dotted",
                                textUnderlineOffset: 3,
                                "&:hover": { color: "primary.dark" },
                                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
                              }}
                            >
                              ${holding.costPerLotWithDividend.toFixed(2)}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {holdings.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <ShowChart
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                尚無持股
              </Typography>
              <Typography variant="body2" color="text.secondary">
                開始您的第一筆交易吧！
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {tabValue === 1 && (
        /* Historical Holdings */
        <Paper sx={{ overflow: "hidden", mb: 3 }}>
          <Box
            sx={{
              p: 3,
              pb: 0,
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              📋 過往紀錄 (已清倉)
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                ml: "auto",
              }}
            >
              <ToggleButtonGroup
                size="small"
                color="primary"
                exclusive
                value={historicalHoldingsView}
                onChange={handleHistoricalHoldingsViewChange}
              >
                <ToggleButton value="card" aria-label="card-view">
                  <ViewModule sx={{ fontSize: 18, mr: 1 }} />
                  卡片
                </ToggleButton>
                <ToggleButton value="table" aria-label="table-view">
                  <TableRows sx={{ fontSize: 18, mr: 1 }} />
                  表格
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {historicalHoldingsView === "table" ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>代號/名稱</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      總買入股數
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      總賣出股數
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      平均買價
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      平均賣價
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      總買入金額
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      總賣出金額
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      股利收入
                    </TableCell>
                    <StatHeaderCell
                      label="已實現損益"
                      qualifier="價差"
                      tooltip="賣出淨收入 - 賣出成本，不含股利"
                    />
                    <StatHeaderCell
                      label="總收益"
                      qualifier="含息"
                      tooltip="已實現損益 + 股利收入"
                    />
                    <StatHeaderCell
                      label="報酬率"
                      qualifier="價差"
                      tooltip="已實現損益 ÷ 賣出成本，不含股利"
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historicalHoldings.map((historical) => (
                    <TableRow
                      key={`${historical.symbol}-historical`}
                      sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}
                    >
                      <TableCell>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="primary"
                          >
                            {historical.symbol}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {historical.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="medium">
                          {historical.totalBuyQuantity.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="medium">
                          {historical.totalSellQuantity.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          ${historical.avgBuyPrice.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          ${historical.avgSellPrice.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          $
                          {Math.round(
                            historical.totalBuyAmount,
                          ).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          $
                          {Math.round(
                            historical.totalSellAmount,
                          ).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" color="primary">
                          ${Math.round(historical.dividends).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          color={
                            historical.realizedGain >= 0
                              ? "success.main"
                              : "error.main"
                          }
                        >
                          {historical.realizedGain >= 0 ? "+" : ""}$
                          {Math.round(historical.realizedGain).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          color={
                            historical.totalProfit >= 0
                              ? "success.main"
                              : "error.main"
                          }
                        >
                          {historical.totalProfit >= 0 ? "+" : ""}$
                          {Math.round(historical.totalProfit).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${historical.realizedGainPercent >= 0 ? "+" : ""}${historical.realizedGainPercent.toFixed(2)}%`}
                          color={
                            historical.realizedGainPercent >= 0
                              ? "success"
                              : "error"
                          }
                          variant={
                            Math.abs(historical.realizedGainPercent) > 10
                              ? "filled"
                              : "outlined"
                          }
                          size="small"
                          sx={{ fontWeight: "bold" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ px: 3, pb: 3 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {historicalHoldings.map((historical) => (
                  <Card
                    key={`${historical.symbol}-card`}
                    variant="outlined"
                    sx={{ height: "100%" }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        p: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="primary"
                          >
                            {historical.symbol}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {historical.name}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${historical.realizedGainPercent >= 0 ? "+" : ""}${historical.realizedGainPercent.toFixed(2)}%`}
                          color={
                            historical.realizedGainPercent >= 0
                              ? "success"
                              : "error"
                          }
                          variant={
                            Math.abs(historical.realizedGainPercent) > 10
                              ? "filled"
                              : "outlined"
                          }
                          size="small"
                          sx={{ fontWeight: "bold", fontSize: "1rem" }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-around",
                          py: 1,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="body2" color="text.secondary">
                            已實現損益
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color={
                              historical.realizedGain >= 0
                                ? "success.main"
                                : "error.main"
                            }
                          >
                            {historical.realizedGain >= 0 ? "+" : ""}$
                            {Math.round(
                              historical.realizedGain,
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="body2" color="text.secondary">
                            總收益
                            <QualifierTag text="含息" />
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color={
                              historical.totalProfit >= 0
                                ? "success.main"
                                : "error.main"
                            }
                          >
                            {historical.totalProfit >= 0 ? "+" : ""}$
                            {Math.round(
                              historical.totalProfit,
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          gap: { xs: 2, md: 1 },
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, justifyContent: "space-between", textAlign: { xs: "left", md: "center" }, flex: 1, minWidth: { xs: "auto", md: 120 } }}>
                          <Typography variant="body2" color="text.secondary">
                            買入/賣出
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {historical.totalBuyQuantity.toLocaleString()} /{" "}
                            {historical.totalSellQuantity.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, justifyContent: "space-between", textAlign: { xs: "left", md: "center" }, flex: 1, minWidth: { xs: "auto", md: 120 } }}>
                          <Typography variant="body2" color="text.secondary">
                            股利
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="primary"
                          >
                            ${Math.round(historical.dividends).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {historicalHoldings.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Archive sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                暫無過往紀錄
              </Typography>
              <Typography variant="body2" color="text.secondary">
                尚未有完全清倉的股票記錄
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* 分次購買成本明細 Popover */}
      <Popover
        open={costBreakdown !== null}
        anchorEl={costBreakdown?.anchorEl ?? null}
        onClose={handleCloseCostBreakdown}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {costBreakdown && (
          <CostBreakdownContent holding={costBreakdown.holding} />
        )}
      </Popover>
    </Box>
  );
};

export default Holdings;
